import { useEffect } from "react";
import { message, Button, Form, Modal } from "antd";
import Loader from "../../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useParams, useNavigate } from "react-router";
import { useAPI } from "../../hooks/useAPI";
import { EventData, TicketData } from "../../models/event";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import TicketForm from "./ticketform";
import {
  formatAllDatesForSubmit,
  convertToDayjsForForm,
} from "../../utils/DateAndTimeConvertor";
import { updateCall, getCall } from "../../constants/apicalls.constant";
import { setSelectedEventList } from "../../store/slices/event.slice";
import { ROUTES } from "../../constants/routes.constant";
import { transformImageFieldsToUIDs } from "../../utils/TransfromImageFieldsToUIDs";
import { IconArrowNarrowLeft } from "@tabler/icons-react";
import { isTicketSoldOut, canMarkTicketSoldOut, getTicketSoldQuantity } from "../../utils/ticket";

const TicketFormPage = () => {
  const event = useSelector((state: RootState) => state.event);
  const currency = useSelector(
    (state: RootState) => state.event?.currency || "INR",
  );

  const disabledTime = event?.end_date || "";
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, ticketId } = useParams<{ id: string; ticketId?: string }>();

  const { loading, error, trigger } = useAPI<{
    status: boolean;
    message: string;
    data: EventData;
  }>(ENDPOINTS.event);

  const { trigger: fetchEventTrigger } = useAPI<{
    status: boolean;
    data: EventData;
  }>(ENDPOINTS.event);

  useEffect(() => {
    if (!id) return;

    fetchEventTrigger(getCall({}, id)).then((res) => {
      if (res?.status && res.data) {
        dispatch(setSelectedEventList(res.data));
      }
    });
  }, [id, dispatch, fetchEventTrigger]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  const NUMERIC_TICKET_FIELDS = [
    "price",
    "quantity",
    "min_per_transaction",
    "max_per_transaction",
    "min_age",
    "max_age",
  ] as const;

  const coerceTicketNumbers = (obj: Record<string, any>) => {
    const result = { ...obj };

    NUMERIC_TICKET_FIELDS.forEach((field) => {
      if (
        result[field] !== undefined &&
        result[field] !== null &&
        result[field] !== ""
      ) {
        const parsed = Number(result[field]);
        if (!isNaN(parsed)) result[field] = parsed;
      }
    });

    return result;
  };

  const onSubmit = async (values: TicketData) => {
    if (!id) {
      message.error("Event ID missing");
      return;
    }

    try {
      const formattedValues = coerceTicketNumbers(
        formatAllDatesForSubmit(values) as Record<string, any>,
      );

      const existingTickets = event.tickets || [];
      const ticket = formattedValues as TicketData;

      const updatedTickets: TicketData[] = ticketId
        ? existingTickets.map((t) => {
            if (t.id === ticketId) {
              const quantityDiff = (ticket.quantity || 0) - (t.quantity || 0);
              const increasedQuantity = quantityDiff > 0;
              const newAvailableQuantity = (t.available_quantity || 0) + quantityDiff;
              const isQuantitySoldOut = Number(ticket.quantity) === 0;

              return {
                ...t,
                ...ticket,
                id: ticketId,
                available_quantity: isQuantitySoldOut
                  ? 0
                  : newAvailableQuantity < 0
                    ? 0
                    : newAvailableQuantity,
                ...(isQuantitySoldOut
                  ? {
                      status: "sold_out" as const,
                      is_sold_out: true,
                      mark_sold_out: true,
                      reopen_ticket: false,
                    }
                  : increasedQuantity
                    ? {
                        status: "active" as const,
                        is_sold_out: false,
                        mark_sold_out: false,
                        reopen_ticket: true,
                      }
                    : {}),
              };
            }
            return t;
          })
        : [
            ...existingTickets,
            Number(ticket.quantity) === 0
              ? {
                  ...ticket,
                  available_quantity: 0,
                  status: "sold_out" as const,
                  is_sold_out: true,
                  mark_sold_out: true,
                }
              : ticket,
          ];

      let valueToSend: EventData = {
        ...event,
        tickets: updatedTickets,
      };

      valueToSend = transformImageFieldsToUIDs<EventData>(valueToSend);

      valueToSend.steps_info = Array.from(
        new Set(["ticket_config", ...(valueToSend.steps_info || [])]),
      );

      const res = await trigger(updateCall<EventData>(id, valueToSend));

      if (res?.status) {
        dispatch(setSelectedEventList(res.data));
        message.success(
          ticketId
            ? "Ticket updated successfully"
            : "Ticket added successfully",
        );
        navigate(ROUTES.ADD_TICKET.path.replace(":id", id));
      } else {
        message.error(res?.message);
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    }
  };

  const markTicketSoldOut = () => {
    if (!id || !ticketId) return;

    Modal.confirm({
      title: "Mark ticket as sold out?",
      content:
        "Available quantity will be set to 0 and this ticket will no longer be available for sale.",
      okText: "Mark Sold Out",
      okButtonProps: { danger: true },
      onOk: async () => {
        const existingTicket = event.tickets?.find((t) => t.id === ticketId);
        if (!existingTicket) {
          message.error("Ticket not found");
          return;
        }

        const soldQty = getTicketSoldQuantity(existingTicket);
        const updatedTickets = (event.tickets || []).map((t) =>
          t.id === ticketId
            ? {
                ...existingTicket,
                mark_sold_out: true,
                is_sold_out: true,
                status: "sold_out" as const,
                available_quantity: 0,
                quantity: soldQty,
              }
            : t,
        );

        let valueToSend: EventData = transformImageFieldsToUIDs({
          ...event,
          tickets: updatedTickets,
        });

        const res = await trigger(updateCall<EventData>(id, valueToSend));

        if (res?.status) {
          dispatch(setSelectedEventList(res.data));
          message.success("Ticket marked as sold out");
          navigate(ROUTES.ADD_TICKET.path.replace(":id", id));
        } else {
          message.error(res?.message || "Failed to mark ticket as sold out");
        }
      },
    });
  };


  useEffect(() => {
    if (!event || !ticketId) return;

    const ticket = event.tickets?.find((t) => t.id === ticketId);

    if (ticket) {
      const { status: _status, ...ticketFields } = convertToDayjsForForm(ticket);
      form.setFieldsValue({
        ...ticketFields,
        inclusions: ticket?.inclusions?.map((item) => item.id) ?? [],
      });
    }
  }, [event, ticketId, form]);

  if (error) return null;

  const editingTicket = event.tickets?.find((t) => t.id === ticketId);
  const soldOut = isTicketSoldOut(editingTicket);
  const soldQty = getTicketSoldQuantity(editingTicket);

  return (
    <Loader spinning={loading}>
    <div className="p-[24px]">
      {/* ── Page Header ── */}
      <div className="flex items-center gap-24 mb-24">
        <Button
          shape="circle"
          icon={<IconArrowNarrowLeft size={18} />}
          variant="filled"
          color="default"
          className="!border shrink-0"
          onClick={() => navigate(ROUTES.ADD_TICKET.path.replace(":id", id!))}
        />
        <div>
          <h2 className="text-[20px] font-semibold text-[#111827] leading-tight">
            {ticketId ? "Edit Ticket" : "Add New Ticket"}
          </h2>
          <p className="text-[13px] text-[#6b7280] mt-[2px]">
            {ticketId
              ? "Update the details for this ticket type"
              : "Configure a new ticket type for your event"}
          </p>
        </div>
      </div>

      <TicketForm
        showInlineForm={true}
        setShowInlineForm={() => {}}
        editID={ticketId || ""}
        setEditID={() => {}}
        onSubmit={onSubmit}
        onMarkSoldOut={markTicketSoldOut}
        showMarkSoldOut={Boolean(
          ticketId && !soldOut && canMarkTicketSoldOut(event),
        )}
        isSoldOut={soldOut}
        eventType={event?.event_type}
        disabledTime={disabledTime}
        eventStartDate={event?.start_date}
        eventEndDate={event?.end_date}
        eventStartTime={event?.start_time}
        eventEndTime={event?.end_time}
        currency={currency}
        form={form}
        soldQty={soldQty}
      />
    </div>
    </Loader>
  );
};

export default TicketFormPage;
